import * as React from "react";
import { ITopicEditorHeaderProps } from "./~types/TopicEditorHeaderProps";
import {
    Button,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
} from "reactstrap";
import Loader from "src/app/shared/components/loader/Loader";
import { LoaderType } from "src/app/shared/components/loader/~types/LoaderType";
import i18n from "src/app/shared/localization/i18n";
import "./TopicEditorHeader.css";
import { IPublishHistory } from "../../~store/models/PublishHistory";
import { getDateTime } from "../../../shared/helpers/date-helper/DateFormats";

const TopicEditorHeader = (props: ITopicEditorHeaderProps) => {
    const [publishMenuOpened, setPublishMenuOpened] =
        React.useState<boolean>(false);

    const _onSynonymsHandler = (): void => {
        props.onSynonymsHandler();
    };

    const _onPublishQuestionsHandler = (): void => {
        setPublishMenuOpened(false);
        props.onPublishQuestions();
    };

    const _onExportPairsHandler = (): void => {
        props.onExportPairs();
    };

    const _onImportPairsHandler = (): void => {
        props.onImportPairs();
    };

    const _onUndoTopicPairChanges = (): void => {
        if (window.confirm(i18n.t("nlp.doWantUndoTopicPairChanges"))) {
            props.onUndoTopicPairChanges();
        }
    };

    const _onChangeTopicPairsVersion = (
        e: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        e.preventDefault();
        if (!props.topicHasUnpublishedChanges) {
            props.onChangeTopicPairsVerion(+e.currentTarget.value);
        } else if (window.confirm(i18n.t("nlp.changeVersionConfirm"))) {
            props.onChangeTopicPairsVerion(+e.currentTarget.value);
        }
    };

    const _getPublishHistoryVersion = (ph: IPublishHistory): string => {
        if (ph.basedOn > 0) {
            return `${ph.version} (${getDateTime(
                ph.createdOn
            )} ${"based on"} V${ph.basedOn})`;
        }
        return ph.version.toString();
    };

    const handleKeyUp = (
        e: React.KeyboardEvent<HTMLButtonElement>,
        callback: () => void
    ) => {
        if (e.key === "Enter") {
            callback();
        }
    };

    return (
        <div className="topic-editor-header">
            <div className="topic-editor-header__content topic-editor-header__content-grow">
                <span className="topic-editor-header__identifier">{`#${props.topicId}`}</span>
                <span className="topic-editor-header__name">
                    {props.topicName}
                </span>
                <span className="topic-editor-header__topic-version">
                    {`V${props.publishHistory.find((ph) => ph.isSelected)
                            ?.version ?? 0
                        }${props.topicHasUnpublishedChanges ? ` (${i18n.t("common.edited")})` : ""}`}
                </span>
            </div>

            <div className="topic-editor-header__content">
                <Button
                    className="topic-editor-header__button"
                    onClick={props.onSwitchDesignerView}
                    onKeyUp={(e) => handleKeyUp(e, props.onSwitchDesignerView)}
                >
                    {i18n.t(
                        props.isDesignerView
                            ? "nlp.listViewButton"
                            : "nlp.designerViewButton"
                    )}
                </Button>
                <Button
                    className="topic-editor-header__button"
                    onClick={_onPublishQuestionsHandler}
                    onKeyUp={(e) => handleKeyUp(e, _onPublishQuestionsHandler)}
                    disabled={!props.topicHasUnpublishedChanges || props.isPublishingQuestions}
                    style={{ width: '150px' }}
                >
                    {props.isPublishingQuestions ? (
                        <div>
                            <Loader
                                type={LoaderType.Grow}
                                customStyle={"custom-spinner"}
                            />
                        </div>
                    ) : (
                        <div className="button-content">{i18n.t("common.publish")}</div>
                    )}
                </Button>
                <Button
                    className="topic-editor-header__button"
                    onClick={_onUndoTopicPairChanges}
                    onKeyUp={(e) =>
                        handleKeyUp(e, _onUndoTopicPairChanges)
                    }
                    disabled={
                        !props.topicHasUnpublishedChanges ||
                        props.publishHistory.findIndex((ph) => ph.isSelected) ==
                            -1
                    }
                >
                    {i18n.t("common.undo")}
                </Button>

                <select
                    className="topic-editor-header__button topic-editor-header__topic-pairs-version"
                    value=""
                    onChange={_onChangeTopicPairsVersion}
                    disabled={(props.publishHistory.find((ph) => ph.isSelected)?.version ?? 0) === 0}
                >
                    <option value="" hidden disabled selected>
                        {i18n.t("common.changeVersion")}
                    </option>
                    {props.publishHistory.map((ph) => {
                        return (
                            <option
                                key={ph.publishHistoryId}
                                disabled={ph.isSelected}
                                value={ph.publishHistoryId}
                            >
                                v{_getPublishHistoryVersion(ph)}
                            </option>
                        );
                    })}
                </select>

                <Dropdown
                    direction="up"
                    className="topic-editor-header__publish-dropdown"
                    isOpen={publishMenuOpened}
                    toggle={() =>
                        setPublishMenuOpened((prevState) => !prevState)
                    }
                >
                    <DropdownToggle>
                        <span>
                            <i className="material-icons">more_vert</i>
                        </span>
                    </DropdownToggle>              
                    {publishMenuOpened && (
                        <DropdownMenu className="topic-editor-header__publish-dropdown-menu">
                            <DropdownItem
                                className="btn topic-editor-header__publish-dropdown-option"
                                onClick={_onSynonymsHandler}
                            >
                                {i18n.t("common.synonyms")}
                            </DropdownItem>
                            <DropdownItem
                                className="btn topic-editor-header__publish-dropdown-option"
                                onClick={_onExportPairsHandler}
                            >
                                {props.isExportingPairs ? (
                                    <Loader
                                        type={LoaderType.Grow}
                                        customStyle={"custom-spinner"}
                                    />
                                ) : (
                                    <span>{i18n.t("common.export")}</span>
                                )}
                            </DropdownItem>
                            <DropdownItem
                                className="btn topic-editor-header__publish-dropdown-option"
                                onClick={_onImportPairsHandler}
                            >
                                {i18n.t("common.import")}
                            </DropdownItem>
                        </DropdownMenu>
                    )}
                </Dropdown>
            </div>
        </div>
    );
};

export default TopicEditorHeader;
